import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useCoverLetters } from '../hooks/useCoverLetters'
import type { CoverLetter, CoverLetterSections } from '../hooks/useCoverLetters'
import { post } from '../lib/api'
import { gettext as _ } from '../lib/i18n'
import { useConfirm, useToast } from '../components/ui'
import styles from './CoverLettersPage.module.css'

const SECTIONS = ['problem', 'strategy', 'execution', 'result'] as const
type SectionKey = (typeof SECTIONS)[number]
const SECTION_LABELS: Record<SectionKey, string> = {
  problem: '문제',
  strategy: '전략',
  execution: '실행',
  result: '결과',
}

function packagingBadgeClass(level: number) {
  if (level <= 33) return styles.packagingLow
  if (level <= 66) return styles.packagingMid
  return styles.packagingHigh
}

function CoverLetterCard({
  cl,
  talentId,
  onUpdate,
  onRemove,
}: {
  cl: CoverLetter
  talentId: string
  onUpdate: (
    id: string,
    data: { sections?: CoverLetterSections; company?: string; packagingLevel?: number },
  ) => Promise<void>
  onRemove: (id: string) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [sections, setSections] = useState<CoverLetterSections>(cl.sections)
  const [saving, setSaving] = useState(false)
  const [pushing, setPushing] = useState(false)
  const toast = useToast()
  const { confirm } = useConfirm()

  const startEdit = () => {
    setSections({ ...cl.sections })
    setEditing(true)
    if (!open) setOpen(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await onUpdate(cl.id, { sections })
      setEditing(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '저장 실패')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    const ok = await confirm({ message: '이 자소서를 삭제하시겠습니까?', danger: true })
    if (!ok) return
    try {
      await onRemove(cl.id)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '삭제 실패')
    }
  }

  const handlePushNotion = async () => {
    setPushing(true)
    try {
      const res = await post<{ notionPageId: string; notionUrl: string }>(
        `/api/v1/talents/${talentId}/notion/push`,
        { type: 'cover_letter', id: cl.id, title: `${cl.company} 자소서` },
      )
      toast.success(_('Pushed to Notion: {url}', { url: res.notionUrl }))
    } catch (e) {
      toast.error(e instanceof Error ? e.message : _('Notion push failed'))
    } finally {
      setPushing(false)
    }
  }

  return (
    <div className={styles.card}>
      <div
        className={styles.cardHeader}
        onClick={() => setOpen(!open)}
      >
        <div className={styles.cardHeaderLeft}>
          <span className={styles.cardCompany}>{cl.company}</span>
          <span className={`${styles.packagingBadge} ${packagingBadgeClass(cl.packagingLevel)}`}>
            포장 {cl.packagingLevel}
          </span>
          <span className={styles.cardDate}>
            {new Date(cl.createdAt).toLocaleDateString('ko-KR')}
          </span>
        </div>
        <div className={styles.cardHeaderRight}>
          <button
            className={styles.iconBtn}
            onClick={(e) => {
              e.stopPropagation()
              startEdit()
            }}
            aria-label="편집"
          >
            ✏️
          </button>
          <button
            className={styles.iconBtn}
            onClick={(e) => {
              e.stopPropagation()
              handleDelete()
            }}
            aria-label={_('Delete')}
          >
            🗑
          </button>
          <button
            className={styles.iconBtn}
            onClick={(e) => {
              e.stopPropagation()
              handlePushNotion()
            }}
            disabled={pushing}
            aria-label={_('Push to Notion')}
            title={_('Push to Notion')}
          >
            {pushing ? '⏳' : '📄'}
          </button>
          <span className={`${styles.expandIcon} ${open ? styles.expandIconOpen : ''}`}>
            ▼
          </span>
        </div>
      </div>

      {open && (
        <div className={styles.cardBody}>
          {editing ? (
            <div className={styles.editForm}>
              <div className={styles.sectionGrid}>
                {SECTIONS.map((key) => (
                  <div key={key} className={styles.section}>
                    <div className={styles.sectionLabel}>{SECTION_LABELS[key]}</div>
                    <textarea
                      className={styles.editTextarea}
                      value={sections[key]}
                      onChange={(e) =>
                        setSections({ ...sections, [key]: e.target.value })
                      }
                      rows={5}
                    />
                  </div>
                ))}
              </div>
              <div className={styles.editActions}>
                <button
                  className={styles.saveBtn}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? '저장 중...' : '저장'}
                </button>
                <button
                  className={styles.cancelBtn}
                  onClick={() => setEditing(false)}
                  disabled={saving}
                >
                  취소
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className={styles.sectionGrid}>
                {SECTIONS.map((key) => (
                    <div key={key} className={styles.section}>
                      <div className={styles.sectionLabel}>{SECTION_LABELS[key]}</div>
                      <div className={styles.sectionText}>{cl.sections[key]}</div>
                    </div>
                  ),
                )}
              </div>

              {cl.charCounts && (
                <div className={styles.charCounts}>
                  {SECTIONS.map((key) => (
                      <div key={key} className={styles.charItem}>
                        {SECTION_LABELS[key]}{' '}
                        <span className={styles.charValue}>{cl.charCounts![key]}</span>자
                      </div>
                    ),
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default function CoverLettersPage() {
  const { talentId } = useParams<{ talentId: string }>()
  const id = talentId ?? 't1'
  const { coverLetters, loading, error, reload, generate, generating, update, remove } =
    useCoverLetters(id)

  const [company, setCompany] = useState('')
  const [packaging, setPackaging] = useState(50)

  const handleGenerate = async () => {
    if (!company.trim() || generating) return
    await generate(company.trim(), packaging)
    setCompany('')
  }

  if (loading) {
    return (
      <>
        <div className={styles.headerRow}>
          <h1 className={styles.title}>맞춤 자소서</h1>
        </div>
        <div className={styles.loadingWrap}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className={styles.skeleton} />
          ))}
        </div>
      </>
    )
  }

  if (error) {
    return (
      <>
        <div className={styles.headerRow}>
          <h1 className={styles.title}>맞춤 자소서</h1>
        </div>
        <div className={styles.errorWrap}>
          <span className={styles.errorMsg}>{error}</span>
          <button className={styles.retryBtn} onClick={reload}>
            다시 시도
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      <div className={styles.headerRow}>
        <h1 className={styles.title}>맞춤 자소서</h1>
      </div>

      <div className={styles.formCard}>
        <div className={styles.formTitle}>AI 자소서 생성</div>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="cl-company">
              지원 회사
            </label>
            <input
              id="cl-company"
              className={styles.formInput}
              type="text"
              placeholder="예: 네이버, 카카오"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </div>

          <div className={styles.formGroupNarrow}>
            <label className={styles.formLabel}>포장 수위</label>
            <div className={styles.sliderWrap}>
              <input
                className={styles.slider}
                type="range"
                min={0}
                max={100}
                value={packaging}
                onChange={(e) => setPackaging(Number(e.target.value))}
              />
              <span className={styles.sliderValue}>{packaging}</span>
            </div>
            <div className={styles.sliderLabels}>
              <span className={styles.sliderLabelLeft}>사실</span>
              <span className={styles.sliderLabelRight}>강조</span>
            </div>
          </div>

          <button
            className={styles.generateBtn}
            onClick={handleGenerate}
            disabled={!company.trim() || generating}
          >
            {generating ? '생성 중...' : '✨ AI로 생성'}
          </button>
        </div>
      </div>

      <div className={styles.banner}>
        <span className={styles.bannerIcon}>💡</span>
        사실을 왜곡하지 않고 강점을 조명합니다
      </div>

      {coverLetters.length === 0 ? (
        <div className={styles.emptyWrap}>
          <div className={styles.emptyIcon}>📝</div>
          <div>아직 생성된 자소서가 없습니다</div>
          <div>위 폼에서 회사를 입력하고 AI로 생성하세요</div>
        </div>
      ) : (
        <div className={styles.list}>
          {coverLetters.map((cl) => (
            <CoverLetterCard
              key={cl.id}
              cl={cl}
              talentId={id}
              onUpdate={update}
              onRemove={remove}
            />
          ))}
        </div>
      )}
    </>
  )
}
